/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useIntl } from 'react-intl-next';

import { IconButton, LinkIconButton, type LinkIconButtonProps } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import DepartmentIcon from '@atlaskit/icon/core/department';
import EmailIcon from '@atlaskit/icon/core/email';
import LocationIcon from '@atlaskit/icon/core/location';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import TeamsAvatar from '@atlaskit/teams-avatar';
import { token } from '@atlaskit/tokens';

import { useAIAnswerContext } from '../ai-answer-dialog/ai-answer-context';
import { SainAction, useAnalytics, useSAINUsageCriteriaMet } from '../common/controller/analytics';
import { messages } from '../messages';

import { onKnowledgeCardLinkClick } from './analytics';
import { KnowledgeCardsTopics } from './knowledge-cards-topics';
import { KnowledgeCardsPicture } from './styled';
import { KnowledgeCardsIntentType } from './types';
import { useTeamsAppLinks } from './use-teams-app-links';
import { onEmailCopy } from './util';

const styles = cssMap({
	knowledgeCardsPersonHeaderWrapper: {
		paddingTop: token('space.250'),
		paddingRight: token('space.250'),
		paddingBottom: token('space.250'),
		paddingLeft: token('space.250'),
	},
	knowledgeCardsTeamHeaderWrapper: {
		paddingTop: token('space.150'),
		paddingLeft: token('space.250'),
		paddingRight: token('space.250'),
		paddingBottom: token('space.150'),
	},
	knowledgeCardsPrimaryInfoWrapper: {
		display: 'flex',
		width: '100%',
		height: '100%',
		flexDirection: 'column',
		justifyContent: 'center',
	},
	knowledgeCardsButtonsWrapper: {
		paddingTop: token('space.100'),
	},
});

type KnowledgeCardsHeaderProps = {
	name: string;
	accountId?: string;
	email?: string | null;
	avatarUrl?: string | null;
	/**
	 * @deprecated pass accountId & allow the URL to be generated by the navigation library
	 */
	profileUrl?: string | null;
	jobTitle?: string | null;
	department?: string | null;
	location?: string | null;
	time?: string | null;
	testId?: string;
} & (
	| { type: KnowledgeCardsIntentType.PERSON }
	| { type: KnowledgeCardsIntentType.TEAM; teamId: string }
);

export const KnowledgeCardsHeader = (props: KnowledgeCardsHeaderProps) => {
	const {
		type,
		name,
		accountId,
		email,
		avatarUrl,
		profileUrl,
		jobTitle,
		department,
		location,
		time,
		testId,
	} = props;
	const { commonAttributes } = useAIAnswerContext();
	const { formatMessage } = useIntl();
	const [fireAnalyticsEvent] = useAnalytics();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const isPerson = type === KnowledgeCardsIntentType.PERSON;
	const subText = isPerson
		? [jobTitle, department].filter((text) => !!text).join(', ')
		: formatMessage(messages.knowledge_cards_team_subheader);
	const trimmedSubText = subText.length > 250 ? `${subText.slice(0, 250)}...` : subText;

	const timeAndLocationComponent = isPerson && (location || time) && (
		<Text size="medium" color="color.text.subtlest">
			<Inline space="space.100" alignBlock="center">
				<LocationIcon label="" />
				<Inline space="space.050">
					{location && <Text>{location}</Text>}
					{time && location && <Text>•</Text>}
					{time && <Text>{time}</Text>}
				</Inline>
			</Inline>
		</Text>
	);

	const tooltipOptions: LinkIconButtonProps['tooltip'] = {
		position: 'bottom',
		content: formatMessage(messages.knowledge_cards_copy_email_address),
		hideTooltipOnClick: true,
	};

	const { cloudId, sessionId } = commonAttributes;
	const showAITopics = isPerson && fg('kd_topics_in_person_card');
	const { profileHref, profileOnNavigate } = useTeamsAppLinks({
		type,
		currentUserId: accountId ?? '',
		cloudId,
		teamId: type === KnowledgeCardsIntentType.TEAM ? props.teamId : undefined,
	});

	const viewText = isPerson
		? messages.ai_answer_who_questions_view_profile
		: messages.knowledge_cards_view_team;

	return (
		<Inline
			space="space.100"
			xcss={
				isPerson ? styles.knowledgeCardsPersonHeaderWrapper : styles.knowledgeCardsTeamHeaderWrapper
			}
			testId={testId}
		>
			<Inline spread="space-between" alignInline="start" alignBlock="center" grow="fill">
				<Inline space="space.200" alignBlock="center">
					{!isPerson && fg('team-avatar-search-card') ? (
						<TeamsAvatar size="xlarge" src={avatarUrl || undefined} name={name} />
					) : (
						<KnowledgeCardsPicture src={avatarUrl || ''} alt={name} />
					)}
					<Box xcss={styles.knowledgeCardsPrimaryInfoWrapper}>
						<Stack space="space.100">
							<Heading size="medium">{name}</Heading>
							{trimmedSubText && (
								<Text size="medium" color="color.text.subtlest">
									<Inline space="space.100" alignBlock="center">
										{isPerson && <DepartmentIcon label="" />} <Text>{trimmedSubText}</Text>
									</Inline>
								</Text>
							)}
							{timeAndLocationComponent}
							{showAITopics && (
								<KnowledgeCardsTopics
									cloudId={cloudId}
									userId={accountId ?? ''}
									userName={name}
									sessionId={sessionId}
									v2styles={true}
								/>
							)}
						</Stack>
					</Box>
				</Inline>
			</Inline>
			<Inline space="space.050" xcss={styles.knowledgeCardsButtonsWrapper}>
				{!!email && (
					<IconButton
						icon={(props) => <EmailIcon {...props} color={token('color.icon')} />}
						label={formatMessage(messages.ai_answer_who_questions_hover_email_icon)}
						onClick={() => {
							fireAnalyticsEvent(
								onKnowledgeCardLinkClick({ ...commonAttributes, location: 'header-copy-email' }),
							);
							fireSAINUsageEvent({ metCriteria: SainAction.COPY_CLICKED, commonAttributes });
							onEmailCopy(email);
						}}
						isTooltipDisabled={false}
						tooltip={tooltipOptions}
					/>
				)}
				<LinkIconButton
					icon={ArrowRightIcon}
					label={formatMessage(viewText)}
					href={fg('platform-adopt-teams-nav-config') ? profileHref : profileUrl || ''}
					onClick={(e) => {
						fireAnalyticsEvent(
							onKnowledgeCardLinkClick({ ...commonAttributes, location: 'header-view-profile' }),
						);
						if (fg('kd_fix_sain_mau_instrumentation')) {
							fireSAINUsageEvent({
								metCriteria: SainAction.VIEW_PROFILE_CLICKED,
								commonAttributes,
							});
						} else {
							fireSAINUsageEvent({ metCriteria: SainAction.VIEW_PROFILE_CLICKED });
						}
						if (fg('platform-adopt-teams-nav-config')) {
							profileOnNavigate(e);
						}
					}}
				/>
			</Inline>
		</Inline>
	);
};
